import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, Optional, ViewEncapsulation } from '@angular/core';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { SimCalendarComponent, SimCalendarView } from './calendar.component';
import { getActiveOffset, yearsPerPage } from './multi-year-view.component';

@Component({
  selector: 'sim-calendar-header',
  templateUrl: './calendar-header.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sim-calendar-header'
  }
})
export class SimCalendarHeaderComponent<D> {
  @Input()
  get currentView(): string {
    return this._currentView;
  }
  set currentView(value: string) {
    if (this._currentView !== value) {
      this._currentView = value;
      this._generatePeriodButtons();
    }
  }

  @Input() enablePrev: boolean;
  @Input() enableNext: boolean;

  get prevButtonLabel(): string {
    return {
      month: '上一月',
      date: '上一月'
    }[this._calendar.currentView];
  }

  get prevMultiButtonLabel(): string {
    return {
      month: '上一年',
      date: '上一年',
      year: '上一年',
      'multi-year': '前20年'
    }[this._calendar.currentView];
  }

  get nextButtonLabel(): string {
    return {
      month: '下一月',
      date: '下一月'
    }[this._calendar.currentView];
  }

  get nextMultiButtonLabel(): string {
    return {
      month: '下一年',
      date: '下一年',
      year: '下一年',
      'multi-year': '后20年'
    }[this._calendar.currentView];
  }

  get selected() {
    return this._calendar.activeDate;
  }

  _periodLabel: string;
  periodButtonLabel: string;

  _periodButtons: Array<{
    label: string;
    title: string;
    clicked: () => void;
    button: boolean;
  }> = [];

  private _currentView: string;

  constructor(
    private _elementRef: ElementRef,
    private _calendar: SimCalendarComponent<D>,
    private _changeDetectorRef: ChangeDetectorRef,
    @Optional() private _dateAdapter: SimDateAdapter<D>
  ) {
    this._calendar.stateChanges.subscribe(() => {
      this._generatePeriodButtons();
      this._changeDetectorRef.markForCheck();
    });
  }

  previousYearClicked() {
    const year = this._calendar.currentView === 'multi-year' ? yearsPerPage : -1;
    this._calendar.activeDate = this._dateAdapter.addYears(this._calendar.activeDate, year);
    this._generatePeriodButtons();
    this._sendActiveDateChange(year);
  }
  previousMonthClicked() {
    this._calendar.activeDate = this._dateAdapter.addMonths(this._calendar.activeDate, -1);
    this._generatePeriodButtons();
    this._sendActiveDateChange(-1);
  }
  nextMonthClicked() {
    this._calendar.activeDate = this._dateAdapter.addMonths(this._calendar.activeDate, 1);
    this._generatePeriodButtons();
    this._sendActiveDateChange(1);
  }
  nextYearClicked() {
    const year = this._calendar.currentView === 'multi-year' ? yearsPerPage : 1;
    this._calendar.activeDate = this._dateAdapter.addYears(this._calendar.activeDate, year);
    this._generatePeriodButtons();
    this._sendActiveDateChange(year);
  }

  periodClicked() {
    const view = {
      month: 'year',
      date: 'month',
      year: 'multi-year'
    }[this._calendar.currentView];
    this._setCurrentView(view);
    this._sendActiveDateChange(view === 'multi-year' ? yearsPerPage : 1);
  }

  private _sendActiveDateChange(num: number) {
    this._calendar.toggleActiveDateChange.next([this._calendar.activeDate, this._calendar.currentView, num]);
  }

  private _setCurrentView(view: SimCalendarView) {
    this._calendar.currentView = view;
    this._generatePeriodButtons();
  }

  private _generatePeriodButtons() {
    switch (this._calendar.currentView) {
      case 'multi-year':
        const activeYear = this._dateAdapter.getYear(this._calendar.activeDate);
        const minYearOfPage =
          activeYear - getActiveOffset(this._dateAdapter, this._calendar.activeDate, this._calendar.minDate, this._calendar.maxDate);
        const maxYearOfPage = minYearOfPage + yearsPerPage - 1;

        this._periodLabel = `${minYearOfPage}年 ~ ${maxYearOfPage}年`;
        this.periodButtonLabel = null;
        break;
      case 'year':
        this._periodLabel = this._dateAdapter.getYear(this._calendar.activeDate) + '年';
        this.periodButtonLabel = '选择年份';
        break;
      case 'month':
        this._periodLabel = this._dateAdapter.format(this._calendar.activeDate, 'yyyy年MM月');
        this.periodButtonLabel = '选择年份';
        break;
      case 'date':
        this._periodLabel = this._dateAdapter.format(this._calendar.activeDate, 'yyyy年MM月dd日');
        this.periodButtonLabel = '选择日期';
        break;
      default:
        break;
    }
  }
}
